import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({ baseURL: API_URL });

// Inject JWT token into every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 - redirect to login
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────
export const authAPI = {
  requestOTP: (email) => api.post('/api/auth/request-otp', { email }),
  verifyOTP: (email, code) => api.post('/api/auth/verify-otp', { email, code })
};

// ─── Users ────────────────────────────────────────────────────────────────
export const usersAPI = {
  getAll: (params) => api.get('/api/users', { params }),
  getMe: () => api.get('/api/users/me'),
  getById: (id) => api.get(`/api/users/${id}`),
  search: (q, role) => api.get('/api/users/search', { params: { q, ...(role ? { role } : {}) } }),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
  purge: (id) => api.delete(`/api/users/${id}/purge`),
  invite: (data) => api.post('/api/users/invite', data)
};

// ─── Clients ──────────────────────────────────────────────────────────────
export const clientsAPI = {
  getAll: (params) => api.get('/api/clients', { params }),
  getById: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients', data),
  update: (id, data) => api.put(`/api/clients/${id}`, data),
  delete: (id) => api.delete(`/api/clients/${id}`)
};

// ─── Projects ─────────────────────────────────────────────────────────────
export const projectsAPI = {
  getAll: (params) => api.get('/api/projects', { params }),
  getById: (id) => api.get(`/api/projects/${id}`),
  create: (data) => api.post('/api/projects', data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  delete: (id) => api.delete(`/api/projects/${id}`),
  addMember: (projectId, data) => api.post(`/api/projects/${projectId}/members`, data),
  removeMember: (projectId, memberId) => api.delete(`/api/projects/${projectId}/members/${memberId}`),
  setWeeklyHours: (id, data) => api.post(`/api/projects/${id}/weekly-hours`, data),
  setWorkSchedule: (id, data) => api.post(`/api/projects/${id}/work-schedule`, data),
  assignClientLead: (id, data) => api.post(`/api/projects/${id}/clientleads`, data),
  removeClientLead: (id, leadId) => api.delete(`/api/projects/${id}/clientleads/${leadId}`),
  addMilestone: (id, data) => api.post(`/api/projects/${id}/milestones`, data),
  updateMilestone: (id, msId, data) => api.put(`/api/projects/${id}/milestones/${msId}`, data),
  deleteMilestone: (id, msId) => api.delete(`/api/projects/${id}/milestones/${msId}`)
};

// ─── Categories ───────────────────────────────────────────────────────────
export const categoriesAPI = {
  getAll: (params) => api.get('/api/categories', { params }),
  create: (data) => api.post('/api/categories', data),
  update: (id, data) => api.put(`/api/categories/${id}`, data),
  delete: (id) => api.delete(`/api/categories/${id}`),
  seed: () => api.post('/api/categories/seed')
};

// ─── Activities ───────────────────────────────────────────────────────────
export const activitiesAPI = {
  getAll: (params) => api.get('/api/activities', { params }),
  getDailySummary: (params) => api.get('/api/activities/daily-summary', { params }),
  getProjectTotals: (params) => api.get('/api/activities/project-totals', { params }),
  getRecent: () => api.get('/api/activities/recent'),
  getById: (id) => api.get(`/api/activities/${id}`),
  create: (data) => api.post('/api/activities', data),
  update: (id, data) => api.put(`/api/activities/${id}`, data),
  delete: (id) => api.delete(`/api/activities/${id}`),
  dispute: (id, data) => api.post(`/api/activities/${id}/dispute`, data),
  autocomplete: (q, projectId) => api.get('/api/activities/autocomplete', { params: { q, projectId } }),
  peopleAutocomplete: (q) => api.get('/api/activities/people-autocomplete', { params: { q } })
};

// ─── Blockages ────────────────────────────────────────────────────────────
export const blockagesAPI = {
  getAll: (params) => api.get('/api/blockages', { params }),
  getById: (id) => api.get(`/api/blockages/${id}`),
  create: (data) => api.post('/api/blockages', data),
  update: (id, data) => api.put(`/api/blockages/${id}`, data),
  resolve: (id) => api.put(`/api/blockages/${id}/resolve`),
  delete: (id) => api.delete(`/api/blockages/${id}`)
};

// ─── Reports ──────────────────────────────────────────────────────────────
export const reportsAPI = {
  getProjectReport: (projectId, params) => api.get(`/api/reports/project/${projectId}`, { params }),
  getMemberReport: (memberId, params) => api.get(`/api/reports/member/${memberId}`, { params }),
  getClientReport: (clientId, params) => api.get(`/api/reports/client/${clientId}`, { params }),
  getDashboard: (params) => api.get('/api/reports/dashboard', { params }),
  getAvailability: (params) => api.get('/api/reports/availability', { params }),
  downloadProjectExcel: (projectId, params) =>
    api.get(`/api/reports/project/${projectId}/excel`, { params, responseType: 'blob' }),
  downloadMemberExcel: (memberId, params) =>
    api.get(`/api/reports/member/${memberId}/excel`, { params, responseType: 'blob' })
};

// ─── AI Analysis ──────────────────────────────────────────────────────────
export const aiAPI = {
  analyze: (payload) => api.post('/api/ai/analyze', payload)
};

// ─── Holidays ─────────────────────────────────────────────────────────────
export const holidaysAPI = {
  getAll: (params) => api.get('/api/holidays', { params }),
  create: (data) => api.post('/api/holidays', data),
  update: (id, data) => api.put(`/api/holidays/${id}`, data),
  delete: (id) => api.delete(`/api/holidays/${id}`)
};

// ─── Files ────────────────────────────────────────────────────────────────
export const filesAPI = {
  upload: (formData, onProgress) => api.post('/api/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  }),
  uploadMultiple: (formData) => api.post('/api/files/upload-multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSignedUrl: (key) => api.get('/api/files/signed-url', { params: { key } }),
  delete: (key) => api.delete('/api/files', { params: { key } })
};

export default api;
